import React from 'react';
import type { JSONContent } from '@tiptap/react';
import { MessageSquare } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { MessagingSplitView } from '../Messaging/MessagingSplitView';
import type { ThreadSummary } from '../Messaging/MessageThreadList';
import type { ThreadMessage, ThreadRead } from '../Messaging/ConversationPanel';

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

export const EmployeeMessages: React.FC = () => {
    const [threads, setThreads] = React.useState<ThreadSummary[]>([]);
    const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(null);
    const [threadMessages, setThreadMessages] = React.useState<ThreadMessage[]>([]);
    const [threadLoading, setThreadLoading] = React.useState(false);
    const [replying, setReplying] = React.useState(false);
    const [viewingAttachmentId, setViewingAttachmentId] = React.useState<string | null>(null);
    const [otherReads, setOtherReads] = React.useState<ThreadRead[]>([]);
    const [recipientCount, setRecipientCount] = React.useState(1);
    const [allowReplies, setAllowReplies] = React.useState(true);

    // window.open() doit s'exécuter de façon SYNCHRONE au clic (avant tout await) sous peine d'être
    // bloqué silencieusement par le navigateur (geste utilisateur perdu après le fetch).
    const viewAttachment = async (messageId: string, win: Window | null) => {
        setViewingAttachmentId(messageId);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/team-announcement-messages/${messageId}/attachment`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { win?.close(); return; }
            const blob = await res.blob();
            if (win) win.location.href = URL.createObjectURL(blob);
        } catch { win?.close(); } finally {
            setViewingAttachmentId(null);
        }
    };

    const load = () => {
        moduleApi.getMyAnnouncements().then((d: any) => setThreads(Array.isArray(d) ? d : [])).catch(() => setThreads([]));
    };
    React.useEffect(() => { load(); }, []);

    const openThread = async (id: string) => {
        setSelectedThreadId(id);
        setThreadLoading(true);
        try {
            const res: any = await moduleApi.getTeamAnnouncement(id);
            setThreadMessages(Array.isArray(res?.messages) ? res.messages : []);
            setOtherReads(Array.isArray(res?.other_reads) ? res.other_reads : []);
            setRecipientCount(res?.recipient_count || 1);
            setAllowReplies(res?.thread?.allow_replies !== 0);
        } catch {
            setThreadMessages([]);
        } finally {
            setThreadLoading(false);
            load();
        }
    };

    const handleReply = async ({ text, rich, attachment }: { text: string; rich: JSONContent; attachment: File | null }) => {
        if (!selectedThreadId) return;
        setReplying(true);
        try {
            const attachmentPayload = attachment ? { display_name: attachment.name, file_name: attachment.name, mime_type: attachment.type, data: await fileToBase64(attachment) } : undefined;
            const res: any = await moduleApi.replyTeamAnnouncement(selectedThreadId, text, rich, attachmentPayload);
            if (res?.error) { alert(res.error); return; }
            const detail: any = await moduleApi.getTeamAnnouncement(selectedThreadId);
            setThreadMessages(Array.isArray(detail?.messages) ? detail.messages : []);
            setOtherReads(Array.isArray(detail?.other_reads) ? detail.other_reads : []);
            setRecipientCount(detail?.recipient_count || 1);
            load();
        } finally {
            setReplying(false);
        }
    };

    const selectedThread = threads.find((t) => t.id === selectedThreadId);

    return (
        <div className="space-y-6">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><MessageSquare size={24} /> Messages</h1>
                <p className="text-content-muted mt-1">Annonces et messages de votre établissement.</p>
            </header>

            <MessagingSplitView
                threads={threads}
                selectedThreadId={selectedThreadId}
                onSelectThread={openThread}
                subtitle={(t) => t.target_employe_id ? '' : "Message à toute l'équipe"}
                emptyListLabel="Aucun message pour l'instant."
                conversationTitle={selectedThread?.title || ''}
                messages={threadMessages}
                threadLoading={threadLoading}
                myAuthorType="employe"
                onReply={handleReply}
                sending={replying}
                onViewAttachment={viewAttachment}
                viewingAttachmentId={viewingAttachmentId}
                emptyConversationLabel="Sélectionnez un message pour l'ouvrir."
                otherReads={otherReads}
                recipientCount={recipientCount}
                allowReplies={allowReplies}
            />
        </div>
    );
};

export default EmployeeMessages;
